import React, { Component, Dispatch } from 'react';
import { connect } from 'react-redux';

import './BasketItem.scss';

import {
    BasketItemModel,
    ProductTypes,
    StockTypes
} from 'models/Basket';
import { NotificationTypes } from 'models';
import { formatCurrency } from 'formatters';
import { BasketSofaglide, BasketSofashield, Loader } from 'components';
import { addNotification, AddNotificationFunc } from 'store/notifications/actions';
import { CareKitInfo, generateDispatchToPropsMap } from 'helpers';
import {
    removeBasketItem,
    RemoveBasketItemFunc,
    removeSofaglide,
    RemoveSofaglideFunc,
    removeSofashield,
    RemoveSofashieldFunc,
    updateBasketItemQuantity,
    UpdateBasketItemQuantityFunc,
    updateSofashieldQuantity,
    UpdateSofashieldQuantityFunc
} from 'store/basket/actions';

import {
    SofaItemOverview,
    SofaItemImage,
    SofaQuantity,
    SofaButton,
} from '@sofology/sofologycomponents';

const VINTAGE_CARE_INSTRUCTION_PATH = 'https://images.sofology.co.uk/image/upload/v1725967945/Bloomreach/Basket/Character_vintage_leather_Sept_24.pdf';
const CHARACTER_VINTAGE_LEATHER_SWATCH = 'CHARACTER VINTAGE LEATHER';

const ITEMS_QUANTITY_CHANGE_DELAY = 500;
const ITEMS_QUANTITY_MIN_VALUE = 0;
const ITEMS_QUANTITY_MAX_VALUE = 99;

interface OwnProps {
    basketItem: BasketItemModel;
    loading: boolean;
    careKitsInfo: CareKitInfo;
}

interface DispatchProps {
    addNotification: AddNotificationFunc;
    updateBasketItemQuantity: UpdateBasketItemQuantityFunc;
    removeBasketItem: RemoveBasketItemFunc;
    removeSofaglide: RemoveSofaglideFunc;
    updateSofashieldQuantity: UpdateSofashieldQuantityFunc;
    removeSofashield: RemoveSofashieldFunc;
}

//  Any changes made here should be synced to BasketItemBundle.tsx / ReadonlyItem.tsx / ReadonlyItemBundle.tsx
class BasketItem extends Component<OwnProps & DispatchProps> {

    handleQuantityChange = (quantity: number) => {
        this.props.updateBasketItemQuantity(this.props.basketItem.product.code, quantity);
    };

    handleQuantityChangeByClick = (value: number, isValid: boolean) => {
        if (!isValid && value > ITEMS_QUANTITY_MAX_VALUE) {
            this.props.addNotification('You cannot buy more than 99 items at once.', NotificationTypes.Err);
        }
    };

    handleRemoveClick = () => {
        this.props.removeBasketItem(this.props.basketItem.product.code);
    };

    handleSofaglideRemoveClick = (sofaglideParentCode: string) => {
        this.props.removeSofaglide(sofaglideParentCode);
    };

    handleChangeSofashieldQuantity = (sku: string, sofashieldQuantity: number) => {
        this.props.updateSofashieldQuantity(sku, sofashieldQuantity);
    };

    handleSofashieldRemoveClick = (sku: string) => {
        this.props.removeSofashield(sku);
    };

    isCharacterVintageLeatherItem = (item: BasketItemModel) => {
        return item.product?.swatchColour?.includes(CHARACTER_VINTAGE_LEATHER_SWATCH)
            || item.product?.swatchName?.includes(CHARACTER_VINTAGE_LEATHER_SWATCH);
    };

    render(): JSX.Element {
        const { basketItem, loading } = this.props;
        const isDonated = basketItem.product.type === ProductTypes.Donation;
        const isSofashield = basketItem.product.type === ProductTypes.Sofashield;
        const isSofaRescue = basketItem.product.type === ProductTypes.SofaRescue;

        const shouldRenderSofaQuantity = !isDonated && !isSofashield && !isSofaRescue;

        return (
            <div className='basket-item'>
                <div className='basket-item__content'>
                    <SofaItemImage product={basketItem.product} enableSofaLink={this.shouldEnableSofaLink(basketItem)} />
                    <div className='basket-item__content__details'>
                        {this.renderItemOverview()}
                        <div className='basket-item__content__controls'>
                            {basketItem.hasDiscount()
                                ? this.renderDiscountedPrice()
                                : this.renderPrice()
                            }
                            {
                                shouldRenderSofaQuantity && <SofaQuantity
                                    value={basketItem.quantity}
                                    minValue={ITEMS_QUANTITY_MIN_VALUE}
                                    maxValue={ITEMS_QUANTITY_MAX_VALUE}
                                    onChange={this.handleQuantityChange}
                                    onClick={this.handleQuantityChangeByClick}
                                    disabled={loading}
                                    hideControls={!basketItem.hasStockType(StockTypes.Default)}
                                    debounce={ITEMS_QUANTITY_CHANGE_DELAY} />
                            }
                            {!isSofashield && <SofaButton onClick={this.handleRemoveClick} variant='text' text='Remove' />}
                        </div>
                    </div>
                    <Loader show={loading} overlay={true} />
                </div>
                {this.isCharacterVintageLeatherItem(basketItem) &&
                    <div className='basket-item__content__controls__vintage-disclaimer'>
                        <span>This product is made from natural leather, left almost completely untouched.
                            Expect charming imperfections as natural features of character vintage leather. </span>
                        <strong><a href={VINTAGE_CARE_INSTRUCTION_PATH} target='_blank' rel='noopener noreferrer'>View full leather details and care instructions.</a></strong>
                    </div>
                }
                <div className='basket-item__content__controls__care-kit-suggestion'>
                    {this.props.careKitsInfo.relatedCareKits.map(careKit => {
                        return (
                            <div key={careKit.name}>
                                <div className='basket-item__content__controls__care-kit-suggestion__title'>
                                    <span>Extra you may need</span>
                                    <strong>{careKit.name}</strong> {this.props.careKitsInfo.isCareKitInCart ? '(Already in cart)' : '(Not in cart)'}
                                </div>
                            </div>
                        );
                    })}
                </div>
                {
                    basketItem.sofashieldQuantity > 0
                    && <BasketSofashield
                        linkedSku={basketItem.product.code}
                        range={basketItem.range?.toLowerCase() ?? ''}
                        onChangeQuantity={this.handleChangeSofashieldQuantity}
                        onRemove={this.handleSofashieldRemoveClick}
                        optionsQuantity={basketItem.quantity}
                        sofashieldQuantity={basketItem.sofashieldQuantity}
                        loading={loading} />
                }

                {
                    basketItem.linkedSofaglide &&
                    <BasketSofaglide
                        onRemove={this.handleSofaglideRemoveClick}
                        priceValue={basketItem.linkedSofaglide.price.value * basketItem.linkedSofaglide.quantity}
                        range={basketItem.range?.toLowerCase() ?? ''}
                        sofaglideParentCode={basketItem.linkedSofaglide.baseProductCode}
                        loading={loading}
                    />
                }
            </div>
        );
    }

    renderItemOverview(): JSX.Element {
        const { basketItem } = this.props;

        return (
            <div className='basket-item__content__details__overview'>
                <SofaItemOverview product={basketItem.product} enableSofaLink={this.shouldEnableSofaLink(basketItem)} />
            </div>
        );
    }

    renderPrice(): JSX.Element {
        const { basketItem } = this.props;

        return (
            <span className='basket-item__content__controls__price'>
                {basketItem.totalPrice.value !== 0
                    ? formatCurrency(basketItem.totalPrice.value, 0)
                    : <span className='basket-item__content__controls__price-free'>FREE</span>
                }
            </span>
        );
    }

    renderDiscountedPrice(): JSX.Element {
        const { basketItem } = this.props;

        return (
            <div className='basket-item__content__controls__discount'>
                <div className='basket-item__content__controls__discount-now'>
                    {formatCurrency(basketItem.totalPrice.value, 0)}
                </div>
                <div className='basket-item__content__controls__discount-was'>
                    was <span>{formatCurrency(basketItem.totalPrice.value + basketItem.savingsPrice.value, 0)}</span>
                </div>
                <div className='basket-item__content__controls__discount-save'>
                    save {formatCurrency(basketItem.savingsPrice.value, 0)}
                </div>
            </div>
        );
    }

    shouldEnableSofaLink = (item: BasketItemModel): boolean => {
        return (Boolean(item.product.range) && item.hasStockType(StockTypes.Default)) || item.product.type === ProductTypes.Sofashield;
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>): DispatchProps {
    return generateDispatchToPropsMap(
        dispatch,
        [
            addNotification,
            removeBasketItem,
            removeSofaglide,
            updateBasketItemQuantity,
            updateSofashieldQuantity,
            removeSofashield
        ]
    );
}

export default connect(undefined, mapDispatchToProps)(BasketItem);